/*
 Copyright 2019-present wobe-systems GmbH

 Licensed under the Apache License, Version 2.0 (the 'License');
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

     http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an 'AS IS' BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.
*/
import { ActionTree } from 'vuex';
import * as types from '@/store/types';
import { logging } from '@/scripts/debugger';
import { OK, SERVICES } from '@/middlewares/httpClient';
import { RootState } from '../models';
import {
  Workspace, WorkspaceState, Member,
} from './models';
import {
  getWorkspaces,
  getWorkspace,
  addMember,
  deleteMember,
  updateWorkspace,
  addWorkspace,
  deleteWorkspace,
} from './apis';

import { handleErrorMessage } from '../handleCatchError';

const log = (message: string) => {
  logging('VUEX/flow/actions', message);
};

const service : string = SERVICES.get('flowmanager');

export const actions: ActionTree<WorkspaceState, RootState> = {
  async [types.FETCH_USER_WORKSPACES]({ commit, dispatch }, userId: string): Promise<any> {
    commit(types.LOADING, true);
    return new Promise((resolve, reject) => {
      getWorkspaces(userId)
        .then(({ status, data }) => {
          if (status === OK) {
            const workspaces: Workspace[] = data;

            commit(types.MUTATE_USER_WORKSPACES, workspaces);
            commit(types.LOADING, false);
            resolve(status);
          }
        })
        .catch((e) => {
          commit(types.LOADING, false);
          handleErrorMessage(e, dispatch, service);
          reject(e);
        });
    });
  },

  async [types.FETCH_WORKSPACE]({ commit, dispatch }, workspaceId: string): Promise<any> {
    if (!workspaceId) {
      return;
    }
    commit(types.LOADING, true);

    await getWorkspace(workspaceId)
      .then(({ status, data }) => {
        if (status === OK) {
          const workspace: Workspace = data;

          commit(types.MUTATE_FETCH_WORKSPACE, workspace);
          commit(types.LOADING, false);
        }
      })
      .catch((e) => {
        commit(types.LOADING, false);
        handleErrorMessage(e, dispatch, service);
      });
  },

  [types.ADD_NEW_MEMBER]({ commit }, email: string) {
    commit(types.MUTATE_NEW_MEMBER, { id: `newID+${email}`, email });
  },

  async [types.UPDATE_WORKSPACE]({ commit, dispatch }, workspace: Workspace): Promise<any> {
    return new Promise((resolve, reject) => {
      updateWorkspace(workspace)
        .then(({ status, data }) => {
          if (status === OK) {
            commit(types.MUTATE_UPDATE_WORKSPACE, data);
            resolve(status);
          }
        })
        .catch((e) => {
          handleErrorMessage(e, dispatch, service);
          reject(e);
        });
    });
  },

  async [types.ADD_NEW_MEMBER]({ commit, dispatch }, { workspaceId, email }): Promise<any> {
    commit(types.LOADING, true);

    await addMember(workspaceId, email)
      .then(({ status, data }) => {
        if (status === OK) {
          const newMember: Member = data;

          commit(types.MUTATE_NEW_MEMBER, newMember);
          commit(types.LOADING, false);
        }
      })
      .catch((e) => {
        commit(types.LOADING, false);
        handleErrorMessage(e, dispatch, service);
      });
  },

  async [types.REMOVE_MEMBER]({ commit, dispatch }, { workspaceId, memberId }): Promise<any> {
    return new Promise((resolve, reject) => {
      deleteMember(workspaceId, memberId)
        .then(({ status }) => {
          if (status === OK) {
            commit(types.MUTATE_REMOVE_MEMBER, { memberId, workspaceId });
            commit(types.LOADING, false);
            resolve(status);
          }
        })
        .catch((e) => {
          commit(types.LOADING, false);
          handleErrorMessage(e, dispatch, service);
          reject(e);
        });
    });
  },

  async [types.ADD_WORKSPACE]({ commit, dispatch }, { name, owner }): Promise<any> {
    return new Promise((resolve, reject) => {
      addWorkspace(name, owner)
        .then(({ status, data }) => {
          if (status === OK) {
            commit(types.MUTATE_NEW_WORKSPACE, data);
            resolve(status);
          }
        })
        .catch((e) => {
          handleErrorMessage(e, dispatch, service);
          reject(e);
        });
    });
  },

  async [types.REMOVE_WORKSPACE]({ commit, dispatch }, workspaceId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      deleteWorkspace(workspaceId)
        .then(({ status }) => {
          if (status === OK) {
            commit(types.MUTATE_REMOVE_WORKSPACE, workspaceId);
            resolve(status);
          }
        })
        .catch((e) => {
          handleErrorMessage(e, dispatch, service);
          reject(e);
        });
    });
  },
};
